import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { exhaustMap, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { LocationService } from '@fantaz/core/utils';
import { AuthFacade, getCurrentUser } from '@fantaz/shared/auth';
import {
  AuthServiceInjectionToken,
  CaptureLocationService,
  DevToolDetectionService,
  FzGoogleTagManagerService,
  MatchMakingService,
} from '@fantaz/shared/data-access/api';
import {
  CasualTournamentDetails,
  User,
} from '@fantaz/shared/data-access/models';
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import { BlockDeviceService } from '@fantaz/shared/utils/block-device';
import { MatchMakingDialogComponent } from '@fantaz/tournaments/ui/match-making-dialog';

import { PassesFacade } from '../passes';
import * as PlayTournamentActions from './play-tournament.actions';

@Injectable()
export class PlayTournamentEffects {
  authService = inject(AuthServiceInjectionToken);

  /**
   * Effect to handle play tournament start, validate user, wallet balance,
   * location and open match making dialog
   */
  playTournament$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlayTournamentActions.playTournament),
      withLatestFrom(
        this.authFacade.authUser$,
        this.authFacade.membership$,
        (payload, user, membership) => ({
          ...payload,
          user,
          membership,
          gameIsBlocked: payload.game
            ? this.blockDeviceService.isBlocked(payload.game?.blockedDevices)
            : false,
        }),
      ),
      switchMap((payload) => {
        /*
         * Validate If user is not authenticated
         */
        if (!payload.user) {
          return [
            PlayTournamentActions.playTournamentFailure({
              error: new Error('UNAUTHENTICATED'),
              payload,
            }),
          ];
        }

        if (payload.user.walletFrozen) {
          return [
            PlayTournamentActions.playTournamentFailure({
              error: new Error(
                `WALLET_FROZEN_${
                  payload.user.walletFrozenReason || 'BY_ADMIN'
                }`,
              ),
            }),
          ];
        }

        if (payload.zboOnly) {
          if (
            !payload.membership ||
            !payload.membership.type ||
            payload.membership.type.toLowerCase() !== 'zbo'
          ) {
            return [
              PlayTournamentActions.playTournamentFailure({
                error: new Error('ZBO_ONLY'),
              }),
            ];
          }
        }

        if (payload.gameIsBlocked) {
          return [
            PlayTournamentActions.playTournamentFailure({
              error: new Error('GAME_BLOCKED'),
            }),
          ];
        }

        let isFree = payload.entryAmount === 0;

        if (payload.pass instanceof Object && !isFree) {
          if (payload.pass?.price) {
            isFree = Number(payload.pass.price.price) === 0;
          }
        }

        if (
          !isFree &&
          !payload.pass &&
          Number(payload.user.balance) < payload.entryAmount
        ) {
          return [
            PlayTournamentActions.playTournamentFailure({
              error: new Error('WALLET_BALANCE'),
              payload,
            }),
          ];
        }

        // In case of user start play tournament free or with a pass for free
        // we need to check if user has a negative balance or a loan balance
        // and if so we need to show the error `WALLET_BALANCE_NEGATIVE`
        if (
          (isFree ||
            (payload.pass instanceof Object &&
              payload.pass.price.price == '0')) &&
          Number(payload.user.balance) < 0
        ) {
          PlayTournamentActions.playTournamentFailure({
            error: new Error('WALLET_BALANCE_NEGATIVE'),
            payload,
          });
        }

        return this.locationService.observeLocation().pipe(
          switchMap((res) => {
            if (res.deviceGPS || res.userAllowToUseFallbackLocation) {
              return [
                PlayTournamentActions.playTournamentSuccess({
                  ...payload,
                  user: payload.user as User,
                  userLocation: {
                    ...res,
                  },
                }),
              ];
            }
            return [
              PlayTournamentActions.playTournamentFailure({
                error: new Error('Unauthorized access location'),
                ...payload,
              }),
            ];
          }),
        );
      }),
    ),
  );

  /**
   * Effect to handle match making dialog on play tournament success
   *
   * @param actions$
   */
  openMatchMakingDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PlayTournamentActions.playTournamentSuccess),
        exhaustMap((payload) => {
          this.devToolDetectionService.startDevtoolDetector();
          this.gtmService.pushTag({
            event: 'user-prepare-game',
            userId: payload.user.uuid,
            user_name: payload.user.username,
            sponsor: payload.user.sponsorUsername,
          });
          const dialogRef = this.dialog.open(MatchMakingDialogComponent, {
            disableClose: true,
            autoFocus: true,
            data: {
              user: payload.user,
              openTournament: payload.openTournament,
              scheduleSelected: payload.eventSchedule,
              pass: payload.pass,
              userLocation: payload.userLocation,
              eventCode: payload.eventCode,
            },
          });

          dialogRef
            .afterClosed()
            .pipe(
              map((successResponse) => {
                return successResponse;
              }),
            )
            .subscribe((successResponse) => {
              if (
                successResponse &&
                (successResponse === 'redirectToRoom' ||
                  successResponse === 'refreshPassInfo')
              ) {
                if (
                  payload.openTournament.lifeExperience &&
                  (payload.openTournament as CasualTournamentDetails)
                    .detailsForEventSchedule[0]
                ) {
                  this.passesFacade.load({
                    eventPrototypeUuid: (
                      payload.openTournament as CasualTournamentDetails
                    ).eventPrototypeUuid,
                    eventScheduleUuid: (
                      payload.openTournament as CasualTournamentDetails
                    ).detailsForEventSchedule[0].eventScheduleUuid,
                    userId: this.authService.getUserId(),
                  });
                }
                return;
              }
              if (successResponse) {
                this.matchMakingService.playCasualTournament(successResponse);
              }
            });
          return of();
        }),
      ),
    { dispatch: false },
  );

  /**
   * Effect to handle error on play tournament
   *
   * @param actions$
   */
  showDialogError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PlayTournamentActions.playTournamentFailure),
        withLatestFrom(this.store.select(getCurrentUser)),
        tap(([{ error, payload }, currentUser]) => {
          console.log('error', error);
          this.dialog.open(ModalAlertComponent, {
            data: {
              modalType: (error as unknown as Error).message,
              currentUser: currentUser?.user,
              tournament: {
                eventPrototypeUuid: payload?.eventPrototypeUuid,
                lifeExperience: payload?.openTournament?.lifeExperience,
                eventScheduleUuid: payload?.eventSchedule?.uuid,
                entryAmount: payload?.entryAmount,
              },
            },
          });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private readonly dialog: MatDialog,
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly authFacade: AuthFacade,
    private readonly captureLocation: CaptureLocationService,
    private readonly locationService: LocationService,
    private readonly matchMakingService: MatchMakingService,
    private readonly blockDeviceService: BlockDeviceService,
    private readonly gtmService: FzGoogleTagManagerService,
    private passesFacade: PassesFacade,
    private devToolDetectionService: DevToolDetectionService,
  ) {}
}
